// CT-1163: pure resolution logic for child-org partner branding + identity labels.
//
// Given an org object (as returned by /getOrg — carrying the org's own branding,
// its partner-branding settings, and the resolved `managingPartner` brand), this
// module resolves:
//   1. which brand the app shell should show (own → inherited partner → ChaosTrack), and
//   2. whether the "Managed by <Partner>" and "Powered by ChaosTrack" labels show.
//
// Kept dependency-free (no React, no MUI) so it can be unit-tested as pure functions
// and reused by the Header, AppLayout theming, and the side-panel labels.

// Effective brand-source identifiers.
export const BRAND_SOURCE = {
  OWN: "own", // the child's own logo/brandColor
  PARTNER: "partner", // the inherited managing-partner brand
  CHAOSTRACK: "chaostrack", // the ChaosTrack default (no custom brand in play)
}

const hasValue = (v) => !!(v && String(v).trim())

// True when the org has set its OWN branding (either a logo or a brand color).
export const hasOwnBrand = (org) => hasValue(org?.logoURL) || hasValue(org?.brandColor)

// True when the managing partner has usable custom branding (logo or brand color).
export const partnerHasBrand = (org) => hasValue(org?.managingPartner?.logoURL) || hasValue(org?.managingPartner?.brandColor)

/**
 * Resolve the effective brand for the current org context.
 *
 * Precedence (per spec): child's OWN brand › inherited partner brand › ChaosTrack default.
 * The inherited partner brand applies when: inheritPartnerBranding is not explicitly OFF
 * (unset/null defaults to auto-inherit), the child has NO own brand, and the managing
 * partner actually has custom branding.
 *
 * Returns:
 *   {
 *     source: BRAND_SOURCE.*,
 *     logoURL: string|null,      // brand logo to show (null → caller falls back to default asset)
 *     brandColor: string|null,   // brand color to apply (null → default theme)
 *     partnerName: string|null,  // managing partner's display name (for the label), when known
 *   }
 */
export const resolveEffectiveBrand = (org) => {
  const partnerName = org?.managingPartner?.displayName ?? null

  if (hasOwnBrand(org)) {
    return {
      source: BRAND_SOURCE.OWN,
      logoURL: hasValue(org?.logoURL) ? org.logoURL : null,
      brandColor: hasValue(org?.brandColor) ? org.brandColor : null,
      partnerName,
    }
  }

  if (org?.inheritPartnerBranding !== false && partnerHasBrand(org)) {
    return {
      source: BRAND_SOURCE.PARTNER,
      logoURL: hasValue(org?.managingPartner?.logoURL) ? org.managingPartner.logoURL : null,
      brandColor: hasValue(org?.managingPartner?.brandColor) ? org.managingPartner.brandColor : null,
      partnerName,
    }
  }

  return { source: BRAND_SOURCE.CHAOSTRACK, logoURL: null, brandColor: null, partnerName }
}

// Smart-default label visibility for a given brand source (before per-org overrides).
//   PARTNER brand shown    → "Powered by ChaosTrack" ON,  "Managed by X" OFF
//   CHAOSTRACK default     → "Managed by X" ON,           "Powered by ChaosTrack" OFF
//   CHILD's OWN brand      → BOTH ON
const smartLabelDefaults = (source) => {
  switch (source) {
    case BRAND_SOURCE.PARTNER:
      return { managedBy: false, poweredBy: true }
    case BRAND_SOURCE.OWN:
      return { managedBy: true, poweredBy: true }
    case BRAND_SOURCE.CHAOSTRACK:
    default:
      return { managedBy: true, poweredBy: false }
  }
}

// Apply a tri-state override (true/false = explicit, null/undefined = use default).
const applyOverride = (override, fallback) => (override === true || override === false ? override : fallback)

/**
 * Resolve which identity labels show at the bottom of the side panel.
 *
 * A "Managed by X" label only makes sense when there IS a managing partner with a
 * display name; likewise "Powered by ChaosTrack" is suppressed if there's nothing to
 * anchor it. Overrides (showManagedByLabel / showPoweredByChaostrack) win over the
 * smart default for their respective label.
 *
 * Returns { showManagedBy, showPoweredBy, partnerName, source }.
 */
export const resolveIdentityLabels = (org) => {
  const brand = resolveEffectiveBrand(org)
  const defaults = smartLabelDefaults(brand.source)

  const wantManagedBy = applyOverride(org?.showManagedByLabel, defaults.managedBy)
  const wantPoweredBy = applyOverride(org?.showPoweredByChaostrack, defaults.poweredBy)

  // "Managed by" requires a named managing partner to be meaningful.
  const showManagedBy = wantManagedBy && !!brand.partnerName

  return {
    showManagedBy,
    showPoweredBy: wantPoweredBy,
    partnerName: brand.partnerName,
    source: brand.source,
  }
}
